// AICA ChannelSettingsTab — Project-level channel configuration
// Allows enabling/disabling call and chat channels with org-level inheritance

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar';
import { useReportUnsavedSection } from '@/components/settings/UnsavedChangesGuard';

interface ResolvedChannel {
  enabled: boolean;
  source: 'org' | 'project' | 'default';
  canOverride: boolean;
}

interface ChannelSettingsResponse {
  call: ResolvedChannel;
  chat: ResolvedChannel;
  orgDefaults: { call: boolean; chat: boolean };
}

// Local override state: true/false = explicit, null = inherit from org
interface LocalChannelState {
  call: boolean | null;
  chat: boolean | null;
}

const CHANNELS = ['call', 'chat'] as const;
type ChannelKey = (typeof CHANNELS)[number];

export function ChannelSettingsTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation(['projects', 'common']);
  const { apiClient } = useApiClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedOrgId, isOrgSwitching } = useRole();
  const actionsDisabled = !selectedOrgId || isOrgSwitching;

  // null means "not yet initialized from server"
  const [local, setLocal] = useState<LocalChannelState | null>(null);

  const { data, isLoading } = useQuery<ChannelSettingsResponse>({
    queryKey: ['channel-settings', selectedOrgId, projectId],
    queryFn: () =>
      apiClient.projects.getChannelSettings(
        projectId
      ) as unknown as Promise<ChannelSettingsResponse>,
    enabled: !!selectedOrgId,
  });

  useEffect(() => setLocal(null), [selectedOrgId]);

  // Initialize local state once when data first arrives
  useEffect(() => {
    if (data && local === null) {
      setLocal({
        call: data.call.source === 'project' ? data.call.enabled : null,
        chat: data.chat.source === 'project' ? data.chat.enabled : null,
      });
    }
  }, [data, local]);

  // What the server currently holds, in the same shape as `local`. The edits
  // live only in this component, so without reporting them the screen's
  // UnsavedChangesGuard cannot see them — and a section switch unmounts the
  // tab, taking them with it.
  const serverState = data
    ? {
        call: data.call.source === 'project' ? data.call.enabled : null,
        chat: data.chat.source === 'project' ? data.chat.enabled : null,
      }
    : null;
  useReportUnsavedSection(
    'project-settings:channels',
    local !== null &&
      serverState !== null &&
      (local.call !== serverState.call || local.chat !== serverState.chat)
  );

  const saveMutation = useMutation({
    mutationFn: (settings: { call?: boolean | null; chat?: boolean | null }) => {
      if (actionsDisabled) throw new Error('Organization switch in progress');
      return apiClient.projects.updateChannelSettings(projectId, settings);
    },
    onSuccess: () => {
      toast.success(t('projects.channelSettingsSaved', 'Channel settings saved'));
      void queryClient.invalidateQueries({
        queryKey: ['channel-settings', selectedOrgId, projectId],
      });
      // Reset local state so it reinitializes from fresh server data
      setLocal(null);
    },
    onError: (err) => {
      logError(
        'channel_settings.save_failed',
        { projectId },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('projects.channelSettingsSaveFailed', 'Failed to save channel settings')
      );
    },
  });

  const handleToggle = (channel: ChannelKey, enabled: boolean) => {
    setLocal((prev) => ({ ...(prev ?? { call: null, chat: null }), [channel]: enabled }));
  };

  const handleInherit = (channel: ChannelKey) => {
    setLocal((prev) => ({ ...(prev ?? { call: null, chat: null }), [channel]: null }));
  };

  if (isLoading || local === null || !data) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div className="rounded-lg border border-border bg-bg-surface overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border text-fg-muted text-sm">
              <th className="px-4 py-3 text-left font-medium">
                {t('projects.channelType', 'Channel')}
              </th>
              <th className="px-4 py-3 text-center font-medium">
                {t('projects.channelEnabled', 'Enabled')}
              </th>
              <th className="px-4 py-3 text-left font-medium">
                {t('projects.channelSource', 'Source')}
              </th>
            </tr>
          </thead>
          <tbody>
            {CHANNELS.map((channel) => {
              const resolved = data[channel];
              const localOverride = local[channel];
              // Effective enabled: use local override if set, otherwise resolved value
              const effectiveEnabled =
                localOverride !== null ? localOverride : resolved.enabled;
              const isInheriting = localOverride === null;

              return (
                <tr key={channel} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-fg text-sm">
                    {t(`projects.channelNames.${channel}`)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch
                      checked={effectiveEnabled}
                      onCheckedChange={(v) => handleToggle(channel, v)}
                      disabled={actionsDisabled || !resolved.canOverride}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isInheriting ? (
                        <Badge variant="default" size="sm">
                          {t('projects.channelInherited', 'Inherited')}
                        </Badge>
                      ) : (
                        <>
                          <Badge
                            variant={effectiveEnabled ? 'success' : 'default'}
                            size="sm"
                          >
                            {t('projects.channelOverride', 'Override')}
                          </Badge>
                          {resolved.canOverride && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleInherit(channel)}
                              disabled={actionsDisabled}
                              className="text-xs text-fg-muted hover:text-fg transition-colors"
                            >
                              {t('projects.channelReset', 'Reset')}
                            </Button>
                          )}
                        </>
                      )}
                      {!resolved.canOverride && (
                        <span className="text-xs text-fg-muted">
                          {t('projects.channelOrgLocked', 'Org locked')}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-fg-muted">
        {t(
          'projects.channelsDescription',
          'Disabled channels skip all processing (transcription + analysis) even if routing matches. Use "Reset" to inherit the organization default.'
        )}
      </p>

      <SettingsSaveBar
        onSave={() => saveMutation.mutate({ call: local.call, chat: local.chat })}
        isSaving={saveMutation.isPending}
        isLoaded={!actionsDisabled}
        buttonTestId="channel-settings-save-button"
      />
    </div>
  );
}
